import { useEffect, useRef } from 'react'
import { useReducedMotion } from '../../../hooks/useReducedMotion'
import candlefishMark from '../../../assets/Candlefish.svg'
import { logoScene, type LogoOcclusion } from './logoScene'

// The Candlefish mark (assets/Candlefish.svg), redrawn as a 1-bit ordered
// dither on a slab that tilts in 3D. Lengths are in "units": the mark is
// MARK_WIDTH units wide, and the sampled region leaves room around it for glow.
const MARK_WIDTH = 44
// The mark's width as a fraction of a narrow viewport, the vertical position
// of its center in the viewport, and how far it bobs (CSS pixels).
const VIEWPORT_FILL = 0.8
const CENTER_Y = 0.44
const BOB = 6
// The dot grid: pitch and dot size in CSS pixels. These stay the same on every
// screen. A finer grid on phones leaves gaps under a pixel wide, which turn
// into a beat pattern whenever the page is shown scaled.
const CELL_SIZE = 3
const DOT_SIZE = 2
const REGION_HALF_WIDTH = 27
const REGION_HALF_HEIGHT = 20
const TEXELS_PER_UNIT = 6
const TEX_W = REGION_HALF_WIDTH * 2 * TEXELS_PER_UNIT
const TEX_H = REGION_HALF_HEIGHT * 2 * TEXELS_PER_UNIT
const GLOW_RADIUS = 2

const FRAME_MS = 1000 / 30
const ENTRY_SECONDS = 0.5
// The mark sits on a slab: the artwork on the face nearest the viewer, a
// fainter copy on the face behind it. Angles are in radians.
const MAX_LEAN = 0.6
const IDLE_LEAN = 0.5
const IDLE_SPIN = 0.14
const FOCAL_LENGTH = 110
const HALF_THICKNESS = 2.4
const FAR_FACE_DENSITY = 0.4
// Fraction of the half-viewport the pointer travels to reach full lean.
const POINTER_REACH = 0.4
// CSS pixels. The pointer must cross the mark's center by this much before it
// turns around, and be this far from the center before the nose re-aims.
const TURN_DEADZONE = 40
const AIM_DEADZONE = 30
// Slack on the drawn region, since perspective enlarges the near edge.
const BOUNDS_SCALE = 1.25
// Cuts the glow off before it thins into a regular lattice of lone dots.
const GLOW_FLOOR = 0.05

// prettier-ignore
const BAYER_8 = [
   0, 32,  8, 40,  2, 34, 10, 42,
  48, 16, 56, 24, 50, 18, 58, 26,
  12, 44,  4, 36, 14, 46,  6, 38,
  60, 28, 52, 20, 62, 30, 54, 22,
   3, 35, 11, 43,  1, 33,  9, 41,
  51, 19, 59, 27, 49, 17, 57, 25,
  15, 47,  7, 39, 13, 45,  5, 37,
  63, 31, 55, 23, 61, 29, 53, 21,
].map((v) => (v + 0.5) / 64)

interface Fields {
  sharp: Float32Array
  glow: Float32Array
  solid: Float32Array
}

// Fill enclosed regions of the rasterized artwork, leaving the source SVG
// untouched. A narrow feather hides Greg behind the body, not just its dots.
function silhouette(sharp: Float32Array): Float32Array {
  const outside = new Uint8Array(sharp.length)
  const queue = new Int32Array(sharp.length)
  let head = 0
  let tail = 1
  outside[0] = 1
  const visit = (index: number) => {
    if (outside[index] || sharp[index] > 0.1) return
    outside[index] = 1
    queue[tail++] = index
  }
  while (head < tail) {
    const i = queue[head++]
    const x = i % TEX_W
    if (x > 0) visit(i - 1)
    if (x < TEX_W - 1) visit(i + 1)
    if (i >= TEX_W) visit(i - TEX_W)
    if (i < sharp.length - TEX_W) visit(i + TEX_W)
  }
  return blur(Float32Array.from(sharp, (value, i) => outside[i] ? value : 1), 1)
}

// Separable box blur, run three times to approximate a gaussian.
function blur(src: Float32Array, radius: number): Float32Array {
  const a = src.slice()
  const b = new Float32Array(src.length)
  const span = radius * 2 + 1
  for (let pass = 0; pass < 3; pass++) {
    for (let y = 0; y < TEX_H; y++) {
      const row = y * TEX_W
      let sum = 0
      for (let x = -radius; x <= radius; x++) sum += x >= 0 && x < TEX_W ? a[row + x] : 0
      for (let x = 0; x < TEX_W; x++) {
        b[row + x] = sum / span
        const add = x + radius + 1
        const drop = x - radius
        sum += (add < TEX_W ? a[row + add] : 0) - (drop >= 0 ? a[row + drop] : 0)
      }
    }
    for (let x = 0; x < TEX_W; x++) {
      let sum = 0
      for (let y = -radius; y <= radius; y++) sum += y >= 0 && y < TEX_H ? b[y * TEX_W + x] : 0
      for (let y = 0; y < TEX_H; y++) {
        a[y * TEX_W + x] = sum / span
        const add = y + radius + 1
        const drop = y - radius
        sum += (add < TEX_H ? b[add * TEX_W + x] : 0) - (drop >= 0 ? b[drop * TEX_W + x] : 0)
      }
    }
  }
  return a
}

// Rasterize the mark, centered in the region, and keep its coverage: the lines
// themselves, and a blurred copy for the light they give off.
function buildFields(image: HTMLImageElement): Fields | null {
  const canvas = document.createElement('canvas')
  canvas.width = TEX_W
  canvas.height = TEX_H
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null

  const drawWidth = MARK_WIDTH * TEXELS_PER_UNIT
  const drawHeight = drawWidth * (image.naturalHeight / image.naturalWidth)
  ctx.drawImage(image, (TEX_W - drawWidth) / 2, (TEX_H - drawHeight) / 2, drawWidth, drawHeight)

  const { data } = ctx.getImageData(0, 0, TEX_W, TEX_H)
  const sharp = new Float32Array(TEX_W * TEX_H)
  for (let i = 0; i < sharp.length; i++) sharp[i] = data[i * 4 + 3] / 255

  return { sharp, glow: blur(sharp, GLOW_RADIUS * TEXELS_PER_UNIT), solid: silhouette(sharp) }
}

// Bilinear sample of a field at unit coordinates; zero outside the region.
function sample(field: Float32Array, x: number, y: number): number {
  const fx = (x + REGION_HALF_WIDTH) * TEXELS_PER_UNIT - 0.5
  const fy = (y + REGION_HALF_HEIGHT) * TEXELS_PER_UNIT - 0.5
  const ix = Math.floor(fx)
  const iy = Math.floor(fy)
  if (ix < 0 || iy < 0 || ix >= TEX_W - 1 || iy >= TEX_H - 1) return 0
  const tx = fx - ix
  const ty = fy - iy
  const i = iy * TEX_W + ix
  const top = field[i] + (field[i + 1] - field[i]) * tx
  const bottom = field[i + TEX_W] + (field[i + TEX_W + 1] - field[i + TEX_W]) * tx
  return top + (bottom - top) * ty
}

// The equivalent angle in (-PI, PI].
const wrapAngle = (angle: number) => Math.atan2(Math.sin(angle), Math.cos(angle))

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

export default function DitherMark() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const mask = document.createElement('canvas')
    const maskContext = mask.getContext('2d')
    let maskPixels: ImageData | null = null
    const occlusion: LogoOcclusion = { canvas: mask, left: 0, top: 0, width: 0, height: 0, readyAt: 0 }

    const still = prefersReducedMotion
    let fields: Fields | null = null
    // The canvas is drawn in device pixels so dots land on whole pixels:
    // its size, the dot pitch and dot size, the length of one unit, and the
    // bob distance are all in device pixels.
    let canvasWidth = 0
    let canvasHeight = 0
    let cell = 3
    let dot = 2
    let unit = 9
    let bob = BOB
    // Viewport size and the mark's resting center, in CSS pixels.
    let width = 0
    let height = 0
    let centerX = 0
    let centerY = 0
    let frameId = 0
    let lastFrame = 0
    let start = 0
    // Pointer position in CSS pixels, and the eased pose of the mark: roll
    // turns it in the screen plane, spin rolls it about its own length, and
    // lean swings its nose toward the viewer.
    const pointer = { x: 0, y: 0, active: false }
    const pose = { roll: 0, spin: 0, lean: 0 }
    let aim = 0
    let facingRight = false

    const resize = () => {
      // The canvas is small, so it can afford full resolution on 3x phones.
      const dpr = Math.min(window.devicePixelRatio || 1, 3)
      width = window.innerWidth
      height = window.innerHeight
      const cssUnit = Math.min(9, Math.max(5, (width * VIEWPORT_FILL) / MARK_WIDTH))
      cell = Math.max(2, Math.round(CELL_SIZE * dpr))
      dot = Math.max(1, Math.round(DOT_SIZE * dpr))
      unit = cssUnit * dpr
      bob = BOB * dpr

      // The canvas covers only the circle the mark sweeps (plus its bob), not
      // the whole viewport, so there is far less to clear and composite.
      const reach = Math.hypot(REGION_HALF_WIDTH, REGION_HALF_HEIGHT) * unit * BOUNDS_SCALE + bob
      const side = Math.ceil((reach * 2) / cell) * cell
      const nextWidth = Math.min(side, Math.floor((width * dpr) / cell) * cell)
      const nextHeight = Math.min(side, Math.floor((height * dpr) / cell) * cell)
      // Placed on whole device pixels so the dots stay crisp.
      const left = Math.round(((width - nextWidth / dpr) / 2) * dpr) / dpr
      const top = Math.round((height * CENTER_Y - nextHeight / dpr / 2) * dpr) / dpr
      canvas.style.left = `${left}px`
      canvas.style.top = `${top}px`
      canvas.style.width = `${nextWidth / dpr}px`
      canvas.style.height = `${nextHeight / dpr}px`
      Object.assign(occlusion, { left, top, width: nextWidth / dpr, height: nextHeight / dpr })
      centerX = left + nextWidth / dpr / 2
      centerY = top + nextHeight / dpr / 2
      // Mobile browsers fire resize as the address bar slides; reallocating
      // the canvas then would clear it for nothing.
      if (nextWidth === canvasWidth && nextHeight === canvasHeight) return
      canvasWidth = nextWidth
      canvasHeight = nextHeight
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      mask.width = canvasWidth / cell
      mask.height = canvasHeight / cell
      maskPixels = maskContext?.createImageData(mask.width, mask.height) ?? null
    }

    const draw = (seconds: number) => {
      if (!fields) return
      const { sharp, glow, solid } = fields
      maskPixels?.data.fill(0)

      // The incoming current gathers into the mark over half a second, with
      // one small tail stroke before handing off to the existing idle motion.
      const entry = still ? 1 : smoothstep(0, ENTRY_SECONDS, seconds)
      const drawUnit = unit * (0.96 + 0.04 * entry)
      const swimStrength = 1 + 1.4 * Math.sin(Math.PI * entry)

      // The mark's center within the canvas, bobbing slowly.
      const originX = canvasWidth / 2 + (still ? 0 : Math.sin(seconds * 0.4) * bob)
      const originY = canvasHeight / 2 + (still ? 0 : Math.sin(seconds * 0.63) * bob * 0.66)

      // Point the nose at the pointer. The artwork faces left, so aiming is a
      // roll of the pointer's bearing plus a half turn; on the right that
      // would leave the mark belly-up, so it also spins half a turn about its
      // own length, which mirrors it. Without a pointer, face left and sway.
      if (!still) {
        let targetRoll = 0
        let targetSpin = Math.sin(seconds * 0.37) * IDLE_SPIN
        let targetLean = Math.sin(seconds * 0.5) * IDLE_LEAN
        if (pointer.active) {
          const dx = pointer.x - centerX
          const dy = pointer.y - centerY
          const distance = Math.hypot(dx, dy)
          if (dx > TURN_DEADZONE) facingRight = true
          else if (dx < -TURN_DEADZONE) facingRight = false
          if (distance > AIM_DEADZONE) aim = Math.atan2(dy, dx) + Math.PI
          targetRoll = aim
          if (facingRight) targetSpin += Math.PI
          targetLean = MAX_LEAN * Math.min(1, distance / ((Math.min(width, height) / 2) * POINTER_REACH))
        } else {
          facingRight = false
        }
        pose.roll += wrapAngle(targetRoll - pose.roll) * 0.12
        pose.spin += (targetSpin - pose.spin) * 0.1
        pose.lean += (targetLean - pose.lean) * 0.12
      }
      const roll = pose.roll - 0.06 * (1 - entry)
      const cosRoll = Math.cos(roll)
      const sinRoll = Math.sin(roll)
      const cosSpin = Math.cos(pose.spin)
      const sinSpin = Math.sin(pose.spin)
      const cosLean = Math.cos(pose.lean)
      const sinLean = Math.sin(pose.lean)
      // The slab's axes in screen space (u along the mark, v across it) and
      // its normal, for roll * lean * spin.
      const ux = cosRoll * cosLean
      const uy = sinRoll * cosLean
      const uz = -sinLean
      const vx = cosRoll * sinLean * sinSpin - sinRoll * cosSpin
      const vy = sinRoll * sinLean * sinSpin + cosRoll * cosSpin
      const vz = cosLean * sinSpin
      const nx = cosRoll * sinLean * cosSpin + sinRoll * sinSpin
      const ny = sinRoll * sinLean * cosSpin - cosRoll * sinSpin
      const nz = cosLean * cosSpin
      // Whichever face is toward the viewer carries the solid artwork, so the
      // faint copy stays behind it even when the mark has turned around.
      const nearOffset = nz >= 0 ? HALF_THICKNESS : -HALF_THICKNESS

      // Flicker of the glow. The brief entrance uses the same dots and color
      // as the resting mark, so there is no switch between renderers.
      const flicker = still
        ? 0.5
        : 0.5 + 0.2 * Math.sin(seconds * 7.3) + 0.18 * Math.sin(seconds * 11.7 + 1.3) + 0.12 * Math.sin(seconds * 3.1 + 0.4)
      const entering = !still && seconds < ENTRY_SECONDS
      const intro = entering ? smoothstep(0, 0.09, seconds) : 1

      const colEnd = canvasWidth / cell
      const rowEnd = canvasHeight / cell

      ctx.clearRect(0, 0, canvasWidth, canvasHeight)
      // The brand color comes from the canvas's Tailwind text color, so it
      // follows the light and dark themes.
      ctx.fillStyle = getComputedStyle(canvas).color

      for (let row = 0; row < rowEnd; row++) {
        const screenY = ((row + 0.5) * cell - originY) / drawUnit
        for (let col = 0; col < colEnd; col++) {
          const screenX = ((col + 0.5) * cell - originX) / drawUnit

          // Where the ray from the camera through this dot crosses the near
          // and far faces of the slab, in the artwork's coordinates.
          const along = screenX * ux + screenY * uy
          const across = screenX * vx + screenY * vy
          const facing = screenX * nx + screenY * ny - FOCAL_LENGTH * nz
          const tNear = (nearOffset - FOCAL_LENGTH * nz) / facing
          const tFar = (-nearOffset - FOCAL_LENGTH * nz) / facing
          // Edge-on, a ray can run along the slab and only meet it behind the camera.
          if (tNear <= 0 || tFar <= 0) continue
          const nearDepth = FOCAL_LENGTH * (1 - tNear)
          const farDepth = FOCAL_LENGTH * (1 - tFar)
          const x = tNear * along + nearDepth * uz
          const y = tNear * across + nearDepth * vz
          const farX = tFar * along + farDepth * uz
          const farY = tFar * across + farDepth * vz

          // Swim: a wave travelling tailward, growing from the head back.
          const swim = still ? 0 : swimStrength * (0.1 + 0.9 * smoothstep(-22, 22, x)) * Math.sin(x * 0.12 - seconds * 2.2)
          const farSwim = still ? 0 : swimStrength * (0.1 + 0.9 * smoothstep(-22, 22, farX)) * Math.sin(farX * 0.12 - seconds * 2.2)

          const line = sample(sharp, x, y - swim)
          const farLine = sample(sharp, farX, farY - farSwim)
          const light = sample(glow, x, y - swim)
          if (maskPixels) {
            const coverage = Math.max(sample(solid, x, y - swim), sample(solid, farX, farY - farSwim))
            maskPixels.data[(row * colEnd + col) * 4 + 3] = Math.round(coverage * 255 * entry)
          }
          if (line === 0 && farLine === 0 && light === 0) continue

          const shimmer = still ? 0.5 : 0.5 + 0.5 * Math.sin(x * 0.32 + seconds * 1.9)
          const intensity =
            Math.max(line * 1.15, farLine * FAR_FACE_DENSITY) +
            light * (0.5 + 0.5 * flicker + 0.25 * shimmer) -
            GLOW_FLOOR

          if (intensity * intro > BAYER_8[(row & 7) * 8 + (col & 7)]) {
            let dotX = col * cell
            let dotY = row * cell
            if (entering) {
              // Stable per-cell variation makes a flowing current rather than
              // uniform translation. Every dot lands exactly on its grid cell.
              let hash = Math.imul(col + 1, 374761393) ^ Math.imul(row + 1, 668265263)
              hash = Math.imul(hash ^ (hash >>> 13), 1274126177)
              const seed = ((hash ^ (hash >>> 16)) >>> 0) / 4294967295
              const gathered = smoothstep(0, ENTRY_SECONDS * (0.68 + seed * 0.32), seconds)
              const drift = 1 - gathered
              const travel = Math.min((10 + seed * 14) * unit, (canvasWidth - dot - dotX) * 0.9)
              dotX += drift * travel
              dotY += drift * Math.sin(seed * Math.PI * 2 + gathered * Math.PI) * 5 * unit
            }
            ctx.fillRect(Math.round(dotX), Math.round(dotY), dot, dot)
          }
        }
      }
      if (maskContext && maskPixels) {
        maskContext.putImageData(maskPixels, 0, 0)
        logoScene.current = occlusion
      }
    }

    const redraw = () => draw(still ? 0 : (performance.now() - start) / 1000)

    const tick = (now: number) => {
      frameId = requestAnimationFrame(tick)
      if (now - lastFrame < FRAME_MS) return
      lastFrame = now
      draw((now - start) / 1000)
    }

    // Resizing clears the canvas, so repaint right away instead of leaving it
    // blank until the next frame.
    const onResize = () => {
      resize()
      redraw()
    }
    const onPointerMove = (event: PointerEvent) => {
      pointer.x = event.clientX
      pointer.y = event.clientY
      // A mouse aims the mark wherever it is; a finger only while it is down.
      pointer.active = event.pointerType === 'mouse' || event.pressure > 0
    }
    const onPointerEnd = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') pointer.active = false
    }
    const onPointerLeave = () => {
      pointer.active = false
    }
    // A still mark has no next frame to pick up a theme change.
    const themeObserver = new MutationObserver(redraw)

    const image = new Image()
    image.onload = () => {
      fields = buildFields(image)
      start = performance.now()
      occlusion.readyAt = start + ENTRY_SECONDS * 1000
      if (still) {
        redraw()
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
      } else {
        frameId = requestAnimationFrame(tick)
      }
    }
    image.src = candlefishMark

    resize()
    window.addEventListener('resize', onResize)
    if (!still) {
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerdown', onPointerMove)
      window.addEventListener('pointerup', onPointerEnd)
      window.addEventListener('pointercancel', onPointerEnd)
      document.documentElement.addEventListener('pointerleave', onPointerLeave)
    }

    return () => {
      image.onload = null
      cancelAnimationFrame(frameId)
      themeObserver.disconnect()
      if (logoScene.current === occlusion) logoScene.current = null
      window.removeEventListener('resize', onResize)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerdown', onPointerMove)
      window.removeEventListener('pointerup', onPointerEnd)
      window.removeEventListener('pointercancel', onPointerEnd)
      document.documentElement.removeEventListener('pointerleave', onPointerLeave)
    }
  }, [prefersReducedMotion])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed pointer-events-none -z-10 text-primary-800 dark:text-primary-400"
    />
  )
}
